/**
 * Client Calendar - view repairman availability and book slots
 */
import { useState, useEffect, useCallback } from 'react';
import { format, addDays, startOfWeek, isBefore, startOfDay } from 'date-fns';
import { sr } from 'date-fns/locale';
import {
  ChevronLeft, ChevronRight, Clock, Calendar, Loader2, CheckCircle, X
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API_URL = '';

const DAY_NAMES_SR = ['Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub', 'Ned'];

const ClientCalendar = ({ repairmanId, repairmanName, currentUser }) => {
  const { t } = useTranslation();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [availability, setAvailability] = useState({});
  const [bookings, setBookings] = useState({});
  const [workingHours, setWorkingHours] = useState({ start: 8, end: 18 });
  const [isLoading, setIsLoading] = useState(true);
  const [bookingSlot, setBookingSlot] = useState(null);
  const [description, setDescription] = useState('');
  const [isBooking, setIsBooking] = useState(false);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = startOfDay(new Date());

  const timeSlots = [];
  for (let h = workingHours.start; h < workingHours.end; h++) {
    timeSlots.push(`${String(h).padStart(2, '0')}:00`);
  }

  const fetchCalendar = useCallback(async () => {
    setIsLoading(true);
    try {
      const startDate = format(weekDays[0], 'yyyy-MM-dd');
      const endDate = format(weekDays[6], 'yyyy-MM-dd');
      const res = await axios.get(
        `${API_URL}/api/schedule/calendar/${repairmanId}?start_date=${startDate}&end_date=${endDate}`
      );
      setAvailability(res.data.availability || {});
      setBookings(res.data.bookings || {});
      setWorkingHours({
        start: res.data.working_hours?.start ?? 8,
        end: res.data.working_hours?.end ?? 18
      });
    } catch {
      setAvailability({});
      setBookings({});
    } finally {
      setIsLoading(false);
    }
  }, [repairmanId, weekStart]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const getSlotStatus = (dateStr, time) => {
    const dayBookings = bookings[dateStr] || [];
    if (dayBookings.some(b => b.time === time)) return 'booked';
    const dayAvail = availability[dateStr] || [];
    if (dayAvail.includes(time)) return 'available';
    return 'unavailable';
  };

  const isPast = (dateStr, time) => {
    const [h] = time.split(':').map(Number);
    const slotDate = new Date(dateStr + 'T' + String(h).padStart(2, '0') + ':00:00');
    return isBefore(slotDate, new Date());
  };

  const handleSlotClick = (dateStr, time) => {
    if (!currentUser) {
      toast.error(t('calendar.mustLogin'));
      return;
    }
    if (currentUser.role !== 'customer') {
      toast.error(t('calendar.onlyCustomers'));
      return;
    }
    setBookingSlot({ date: dateStr, time });
    setDescription('');
  };

  const handleBook = async () => {
    if (!bookingSlot) return;
    setIsBooking(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/schedule/book`, {
        repairman_id: repairmanId,
        date: bookingSlot.date,
        time_slot: bookingSlot.time,
        description: description,
        client_name: currentUser?.full_name || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('calendar.bookingSuccess'));
      setBookingSlot(null);
      fetchCalendar();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('calendar.bookingError'));
    } finally {
      setIsBooking(false);
    }
  };

  const prevWeek = () => setWeekStart(prev => addDays(prev, -7));
  const nextWeek = () => setWeekStart(prev => addDays(prev, 7));
  const goToday = () => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

  const canGoPrev = !isBefore(addDays(weekStart, -1), today);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        <span className="ml-2 text-slate-500">{t('calendar.loading')}</span>
      </div>
    );
  }

  const hasAnyAvailability = Object.values(availability).some(slots => slots.length > 0);

  if (!hasAnyAvailability && timeSlots.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <Calendar className="w-10 h-10 mx-auto mb-3 text-slate-300" />
        <p>{t('calendar.noAvailability')}</p>
      </div>
    );
  }

  return (
    <div data-testid="client-calendar">
      {/* Navigation */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={prevWeek} disabled={!canGoPrev}
            data-testid="calendar-prev-week"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={goToday}
            data-testid="calendar-today"
          >
            {t('calendar.today')}
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={nextWeek}
            data-testid="calendar-next-week"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <span className="text-sm font-medium text-slate-700" data-testid="calendar-week-label">
          {format(weekDays[0], 'd. MMM', { locale: sr })} — {format(weekDays[6], 'd. MMM yyyy', { locale: sr })}
        </span>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-3 text-xs text-slate-500">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-emerald-400"></div>
          <span>{t('calendar.free')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-slate-200"></div>
          <span>{t('calendar.unavailable')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-amber-400"></div>
          <span>{t('calendar.booked')}</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm" data-testid="calendar-grid">
          <thead>
            <tr className="bg-slate-50">
              <th className="sticky left-0 bg-slate-50 z-10 px-2 py-2 text-left text-slate-500 font-medium w-16 border-r border-slate-200">
                <Clock className="w-4 h-4 inline" />
              </th>
              {weekDays.map((day, i) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const isToday = dateStr === format(new Date(), 'yyyy-MM-dd');
                return (
                  <th
                    key={i}
                    className={`px-1 py-2 text-center font-medium min-w-[80px] ${
                      isToday ? 'bg-orange-50 text-orange-700' : 'text-slate-600'
                    }`}
                  >
                    <div className="text-xs">{DAY_NAMES_SR[i]}</div>
                    <div className={`text-sm ${isToday ? 'font-bold' : ''}`}>
                      {format(day, 'd')}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map((time, rowIdx) => (
              <tr key={time} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                <td className="sticky left-0 z-10 px-2 py-1.5 text-xs text-slate-500 font-mono border-r border-slate-200 bg-inherit">
                  {time}
                </td>
                {weekDays.map((day, colIdx) => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const status = getSlotStatus(dateStr, time);
                  const past = isPast(dateStr, time);
                  const isAvailable = status === 'available' && !past;

                  let cellClass = 'border-l border-slate-100 px-1 py-1.5 text-center ';
                  if (past) {
                    cellClass += 'bg-slate-100/50 text-slate-300';
                  } else if (status === 'booked') {
                    cellClass += 'bg-amber-100 text-amber-700';
                  } else if (status === 'available') {
                    cellClass += 'bg-emerald-50 hover:bg-emerald-100 cursor-pointer transition-colors';
                  } else {
                    cellClass += 'bg-slate-50 text-slate-300';
                  }

                  return (
                    <td
                      key={colIdx}
                      className={cellClass}
                      onClick={() => isAvailable ? handleSlotClick(dateStr, time) : null}
                      data-testid={`slot-${dateStr}-${time}`}
                    >
                      {status === 'booked' && !past && (
                        <span className="text-[10px] font-medium">{t('calendar.occupied')}</span>
                      )}
                      {isAvailable && (
                        <span className="text-[10px] font-medium text-emerald-600">{t('calendar.free')}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Booking Dialog */}
      {bookingSlot && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="booking-dialog">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {t('calendar.bookingTitle')}
              </h3>
              <button
                onClick={() => setBookingSlot(null)}
                className="text-slate-400 hover:text-slate-600"
                data-testid="booking-dialog-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 mb-5">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-sm text-slate-600">
                  <span className="font-medium text-slate-800">{t('calendar.repairman')}:</span> {repairmanName}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium text-slate-800">{t('calendar.date')}:</span>{' '}
                  {format(new Date(bookingSlot.date), 'EEEE, d. MMMM yyyy', { locale: sr })}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium text-slate-800">{t('calendar.time')}:</span> {bookingSlot.time}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('calendar.descriptionLabel')}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('calendar.descriptionPlaceholder')}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none resize-none"
                  rows={3}
                  data-testid="booking-description"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setBookingSlot(null)}
                className="flex-1"
                disabled={isBooking}
                data-testid="booking-cancel-btn"
              >
                {t('calendar.cancel')}
              </Button>
              <Button
                onClick={handleBook}
                disabled={isBooking}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="booking-confirm-btn"
              >
                {isBooking ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('calendar.booking')}</>
                ) : (
                  <><CheckCircle className="w-4 h-4 mr-2" /> {t('calendar.bookSlot')}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientCalendar;
